import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Subscription } from 'rxjs';
import { ProgressCircleComponent } from '../../../../../components/progress-circle/progress-circle.component';
import { cn } from '../../../../../../utils/utils';
import { UploadedContactsReviewTableComponent } from '../../../../../components/tables/uploaded-contacts-review-table/uploaded-contacts-review-table.component';
import { StepperControlsComponent } from '../../../../../components/ui/stepper-controls/stepper-controls.component';
import { Router } from '@angular/router';
import {
  CsvServiceService,
  IBatchUploadProgress,
} from '../../../../../services/csv/csv-service.service';
import { formatCount } from '../../../../../../utils/sms';

@Component({
  selector: 'csv-review',
  standalone: true,
  imports: [ProgressCircleComponent, UploadedContactsReviewTableComponent, StepperControlsComponent],
  templateUrl: './review.component.html',
  styleUrls: ['../csv-step.css', './review.component.css'],
})
export class ReviewComponent implements OnInit, OnDestroy {
  router = inject(Router);
  csvService = inject(CsvServiceService);

  merge = cn;
  readonly formatCount = formatCount;
  fetchingUploadedContacts: boolean = false;
  uploadError = '';
  batchProgress: IBatchUploadProgress = {
    isBatching: false,
    currentBatch: 0,
    totalBatches: 0,
    percentage: 0,
  };
  private batchProgressSub?: Subscription;

  private _version = -1;
  private _total = 0;
  private _problems = 0;

  private recount() {
    const rows = this.csvService.getValidatedContacts;
    if (this._version === this.csvService.version && this._total === rows.length) return;
    this._total = rows.length;
    this._problems = rows.filter((row) => row.batchLineValidationStatus === 'PROCESSING_ERROR').length;
    this._version = this.csvService.version;
  }

  get totalCount(): number {
    this.recount();
    return this._total;
  }

  get problemCount(): number {
    this.recount();
    return this._problems;
  }

  get readyCount(): number {
    return this.totalCount - this.problemCount;
  }

  get submitLabel(): string {
    const count = this.totalCount;
    return count === 1 ? 'Add 1 contact' : `Add ${formatCount(count)} contacts`;
  }

  ngOnInit() {
    this.batchProgressSub = this.csvService.batchUploadProgress$.subscribe((progress) => {
      this.batchProgress = progress;
      // Show the in-page upload progress whenever batch mode is active.
      this.fetchingUploadedContacts = progress.isBatching;
    });
  }

  ngOnDestroy() {
    this.batchProgressSub?.unsubscribe();
  }

  /** Back to Clean up, where rows are fixed. */
  goToPrevious() {
    this.router.navigate(['organization', 'contacts', 'csv'], {
      queryParams: { title: 'cleanup' },
    });
  }

  goToNext() {
    if (!this.totalCount) return;
    this.uploadError = '';
    this.csvService.uploadAndCreateContacts().subscribe({
      next: () => {
        this.fetchingUploadedContacts = false;
        this.router.navigate(['organization', 'contacts', 'csv'], {
          queryParams: { title: 'feedback' },
        });
      },
      error: () => {
        this.fetchingUploadedContacts = false;
        // A batched upload can stop after some batches were saved. Those rows
        // have left this list, so Add sends only the rest.
        const partial = this.csvService.partialUpload;
        const left = this.totalCount;
        this.uploadError = partial
          ? `The upload stopped part way. The first ${formatCount(partial.sent)} ${
              partial.sent === 1 ? 'row was' : 'rows were'
            } sent and ${formatCount(partial.added)} ${
              partial.added === 1 ? 'contact was' : 'contacts were'
            } added; they are no longer in this list. Check your connection, then select Add to send the other ${formatCount(
              left
            )}. The result will cover both.`
          : 'The contacts were not added. Nothing was lost; check your connection and select Add again.';
      },
    });
  }
}
